import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';

import { fetchMiddlePanelNotesData, resetMiddlePanel } from '../../../../containers/WorkkardDetail/actions/action';
import { MiddlePanelConstant } from "../../../../containers/WorkkardDetail/constants";
import NoteGroup from './noteGroup';
import ListingComponent from "../../../../components/ListingComponent";
import Icons from '../../../../icons';
import { NoteListingLoader } from '../../../../components/SkeletonLoader';
import * as CommonUtil from '../../../../common/util';
import Select from '../../../../components/Select';
import EmptyScreen from '../../../../Emptyscreen';
import NoteFilters from './filters';
import { useLiveRefresh } from '../../../../utils/useLiveRefresh';

const NOTE_DOMAINS = ['note', 'file'];

const Notes = (props) => {
    const dispatch = useDispatch();
    const activityDetail = useSelector(state => state.workkardDetail.activityDetail);
    const middlePanel = useSelector(state => state.workkardDetail.middlePanel);
    const workkard = useSelector(state => state.workkardDetail.workkardDetail);
    const notes = useSelector(state => state.workkardDetail.middlePanel.notes);
    const isLoading = useSelector(state => state.isLoading);
    const publishNoteLoading = useSelector(state => state.workkardDetail.buttonLoaders.publishNote);
    const createNote = useSelector(state => state.workkardDetail.createNote);

    const [reviewFilter, setReviewFilter] = useState(0);
    const [filter, setFilter] = useState(false); // hide and show filter component
    const [showAnimation, setShowAnimation] = useState(false);

    const prevActivityIdRef = useRef(activityDetail.activityId);
    const prevNotesTypeRef = useRef(notes.type);
    const isFirstRenderRef = useRef(true);

    const SeturlHash = (hash) => {
        const redirectPath = {
            pathname: window.location.pathname,
            search: window.location.search,
            hash: `${hash}`
        };
        CommonUtil.redirectURL(props.history, redirectPath);
    };

    const onChangeType = async (selectedValue, reviewFilterValue) => {
        const selectedTab = selectedValue === MiddlePanelConstant.ActivityType ? 'a' : 'c';
        const hash = `${selectedTab}n`;
        SeturlHash(hash);

        const payload = {
            type: 'notes',
            value: selectedValue,
            reviewFilter: reviewFilterValue
        };
        await dispatch(resetMiddlePanel(payload));
    };

    const onScrollHandler = () => {
        const { skip, take } = notes;
        const payload = {};
        payload.request = {
            type: notes.type,
            cId: workkard.Id,
            actId: activityDetail.activityId,
            skip: (skip === 0 && take > 10) ? take : parseInt(skip) + parseInt(take),
            take: (skip === 0 && take > 10) ? 10 : parseInt(take),
            sortBy: notes.type == MiddlePanelConstant.ActivityType ? "0" : "1",
            reviewFilter: notes.reviewFilter,
        };
        if (skip + take < notes.total) {
            dispatch(fetchMiddlePanelNotesData(payload));
        }
    };

    const onClose = () => {
        setFilter(false);
    };

    const refreshNotesRef = useRef(null);
    refreshNotesRef.current = () => {
        if (!workkard?.Id) return;
        const request = {
            type: notes.type,
            cId: workkard.Id,
            actId: activityDetail.activityId || 0,
            skip: 0,
            take: Math.max(parseInt(notes.skip || 0, 10) + parseInt(notes.take || 0, 10), 10),
            sortBy: notes.type == MiddlePanelConstant.ActivityType ? "0" : "1",
            reviewFilter: notes.reviewFilter,
            silent: true,
        };
        if (request.type == MiddlePanelConstant.ActivityType && !request.actId) return;
        dispatch(fetchMiddlePanelNotesData({ request }));
    };

    useLiveRefresh(NOTE_DOMAINS, () => refreshNotesRef.current(), { workkardId: workkard?.Id });

    useEffect(() => {
        if (workkard) {
            const notesType = props.history.location.hash.charAt(2) === 'n' ?
                props.history.location.hash.charAt(1) === 'a' ? MiddlePanelConstant.ActivityType : MiddlePanelConstant.WorkkardType :
                notes.type;
            onChangeType(notesType);

            const payload = {};
            payload.request = {
                type: notesType,
                cId: workkard.Id,
                actId: activityDetail.activityId || 0,
                skip: 0,
                take: 10,
                sortBy: notesType == MiddlePanelConstant.ActivityType ? "0" : "1",
                reviewFilter: notes.reviewFilter
            };
            if ((notesType == MiddlePanelConstant.ActivityType && payload.request.actId > 0) || (notesType == MiddlePanelConstant.WorkkardType)) {
                dispatch(fetchMiddlePanelNotesData(payload));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            prevActivityIdRef.current = activityDetail.activityId;
            prevNotesTypeRef.current = notes.type;
            return;
        }

        const prevActivityId = prevActivityIdRef.current;
        const activityIdChanged = prevActivityId !== activityDetail.activityId;

        // reset to initial state middle panel if switch between activity

        const notesTypeChanged = prevNotesTypeRef.current !== notes.type;
        if (activityIdChanged || notesTypeChanged) {
            // reset to initial state middle panel if switch between activity
            if (activityIdChanged) {
                const resetPayload = {
                    type: 'notes',
                    value: notes.type,
                    reviewFilter: notes.reviewFilter
                };
                dispatch(resetMiddlePanel(resetPayload));
            }
            const payload = {};
            payload.request = {
                type: notes.type,
                cId: workkard.Id,
                actId: activityDetail.activityId,
                skip: 0,
                take: 10,
                sortBy: notes.type == MiddlePanelConstant.ActivityType ? "0" : "1",
                // The user's review filter survives an activity switch; hardcoding
                // '0' here silently discarded it, and disagreed with every other
                // path that fetches this list.
                reviewFilter: notes.reviewFilter
            };
            dispatch(fetchMiddlePanelNotesData(payload));
        }

        // The fetch itself is owned by MiddlePanelContainer's own effect on the
        prevActivityIdRef.current = activityDetail.activityId;
        prevNotesTypeRef.current = notes.type;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityDetail.activityId, notes.type]);

    const renderNotes = (notesData) => {
        if (notesData && notesData.length > 0) {
            const noteIds = notesData.map(item => {
                const date = item.PrettyCreatedDate.split('at');
                return { id: item.Id, date: date[0], time: date[1], data: item };
            });
            const groupNoteIds = _.groupBy(noteIds, 'date');
            const listingProps = {
                data: Object.keys(groupNoteIds).map(key => ({ key, data: groupNoteIds[key] })),
                cellTemplate: NoteGroup,
                onScrollHandler: onScrollHandler,
                onItemClick: () => false,
                type: "div",
                selectedId: '',
                classname: 'scrollArea',
                extraData: notes.newRecord,
                isCellLoading: publishNoteLoading || isLoading,
                cellLoadingTemplate: <div className="notesListing detailListing animated fast fadeIn"><NoteListingLoader count={1} /></div>
            };

            return (
                <React.Fragment>
                    <ListingComponent {...listingProps} />
                </React.Fragment>
            );
        }
        return !isLoading && !publishNoteLoading && <EmptyScreen name="Emptynotes" type={notes.type} />;
    };

    const selectOptions = [
        {
            label: MiddlePanelConstant.Note.ActivityTypeNote,
            value: MiddlePanelConstant.ActivityType
        },
        {
            label: MiddlePanelConstant.Note.WorkkardTypeNote,
            value: MiddlePanelConstant.WorkkardType
        }
    ];

    return (
        <div>
            <div className="filterBlock rowFlex">
                {notes.type === "activity" && !isLoading &&
                    <div className="filterLeft" data-test={`notesFilter ${notes.reviewFilter}`}>
                        <div className="dropDown alignMiddle pd__none">
                            {activityDetail?.activityId && middlePanel?.activityNoteTemplates && middlePanel?.excludedFilters && <a className="alignMiddle black" href="javascript:void(0)" onClick={() => setFilter(!filter)}><Icons name="filter" className="icon mr" /> Filters</a>}
                            {/* */}
                        </div>
                        {filter && middlePanel?.excludedFilters && middlePanel?.activityNoteTemplates && activityDetail?.activityId &&
                            <NoteFilters
                                excludedFilters={middlePanel.excludedFilters[activityDetail.activityTemplateType]}
                                currentNoteTemplates={middlePanel.activityNoteTemplates[activityDetail.activityTemplateType]}
                                reviewfilter={reviewFilter}
                                onSave={(x) => setReviewFilter(x)}
                                OwnerOrgId={activityDetail.ownerOrgId}
                                closeFilter={onClose} />}
                    </div>
                }
                <div className="filterRight" data-test={`notesTypeFilter ${notes.type}`}>
                    <div className="formGroup">
                        <Select
                            options={selectOptions}
                            selected={notes.type}
                            onSelect={async (value) => {
                                await onChangeType(value, notes.reviewFilter);
                                const actId = activityDetail.activityId || 0;
                                const fetchCondition = (value === MiddlePanelConstant.ActivityType && actId > 0) || value === MiddlePanelConstant.WorkkardType;
                                if (fetchCondition) {
                                    // dispatch(fetchMiddlePanelNotesData({
                                    //     request: {
                                    //         type: value,
                                    //         cId: workkard.Id,
                                    //         actId,
                                    //         skip: 0,
                                    //         take: 10,
                                    //         sortBy: value === MiddlePanelConstant.ActivityType ? "0" : "1",
                                    //         reviewFilter: notes.reviewFilter,
                                    //     }
                                    // }));
                                }
                            }}
                            openOnClick={true}
                            iconName="downArrow"
                            isOpenRight={true}
                        />
                    </div>
                </div>
            </div>
            <div className={!isLoading && !publishNoteLoading && notes && notes.data.length < 1 ? "notesMainContainer detailMainContainer noDataFound" : "notesMainContainer detailMainContainer"}>
                {/** loader if isLoading/publishNoteLoading is true and notes data is still waiting for api response */}
                {((isLoading || publishNoteLoading) && notes && notes.data.length < 1) ?
                    <NoteListingLoader count={4} />
                    :
                    (notes
                        &&
                        notes.data
                        &&
                        renderNotes(notes.data))
                }
            </div>
        </div>
    );
};

export default Notes;